import { getChoresClient } from "sj-ui-toolkit/src/client/svcClients";
import type { ApprovedPlaAssignmentRow } from "@/components/pla/types";

const ASSIGNMENT_TYPE_LESSON = "ASSIGNMENT_TYPE_LESSON";
const PROTO_ZERO_TIMESTAMP_PREFIX = "0001-01-01";

type ChoresAssessmentsResponse = Awaited<
  ReturnType<ReturnType<typeof getChoresClient>["getAssessments"]>
>["data"];

type PlaAssessmentReport = NonNullable<NonNullable<ChoresAssessmentsResponse>["reports"]>[number];

export interface PersonalizedLessonAssignmentReportRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  assignmentName: string;
  dueDate: Date | null;
  statusCode?: string | null;
  statusName?: string | null;
  totalLessons: number;
  completedCount: number;
  notStartedCount: number;
  progress: { required: number; requiredPassed: number };
  progressPercentage: number;
  progressDisplay: string;
}

export interface PersonalizedLessonAssignmentMetaRow {
  id: string;
  name: string;
  personalizedLessonAssignment: true;
  personalizedLessonAssignmentPath: string;
  status: string;
  progress: { required: number; requiredPassed: number };
}

const asString = (value: unknown) => (typeof value === "string" ? value : "");

const normalizeTimestamp = (value: string | null | undefined) =>
  value && !String(value).startsWith(PROTO_ZERO_TIMESTAMP_PREFIX) ? value : null;

const toUsableDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return null;
  return date;
};

const isPersonalizedLessonAssignment = (report: PlaAssessmentReport) =>
  report.assignmentType === ASSIGNMENT_TYPE_LESSON;

export const loadApprovedPersonalizedLessonAssignments = async (
  tenantName: string
): Promise<ApprovedPlaAssignmentRow[]> => {
  const { data, error } = await getChoresClient().getAssessments(tenantName);
  if (error) throw error;

  return (data?.reports ?? [])
    .filter(report => isPersonalizedLessonAssignment(report) && !report.archivedDate)
    .map(report => ({
      uuid: report.uuid ?? "",
      assignmentUuid: report.uuid ?? "",
      displayName: `${asString(report.givenName)} ${asString(report.familyName)}`.trim(),
      givenName: asString(report.givenName),
      familyName: asString(report.familyName),
      email: asString(report.email),
      attributes: {},
      lessonsTotal: report.totalLearners || 0,
      approvedAt: normalizeTimestamp(report.startDate),
      endDate: normalizeTimestamp(report.endDate),
    }));
};

export const loadPersonalizedLessonAssignmentReportRows = async (
  tenantName: string
): Promise<PersonalizedLessonAssignmentReportRow[]> => {
  const { data, error } = await getChoresClient().getAssessments(tenantName);
  if (error) throw error;

  return (data?.reports ?? []).filter(isPersonalizedLessonAssignment).map(report => {
    const required = report.totalLearners || 0;
    const requiredPassed = report.completedCount || 0;
    const progressPercentage = required > 0 ? Math.round((requiredPassed / required) * 100) : 0;

    return {
      id: report.uuid ?? "",
      email: asString(report.email),
      firstName: asString(report.givenName),
      lastName: asString(report.familyName),
      assignmentName: asString(report.title),
      dueDate: toUsableDate(report.endDate),
      statusCode: report.overallStatus?.code ?? null,
      statusName: report.overallStatus?.displayName ?? null,
      totalLessons: required,
      completedCount: requiredPassed,
      notStartedCount: report.notStartedCount || 0,
      progress: { required, requiredPassed },
      progressPercentage,
      progressDisplay: required > 0 ? `${progressPercentage}%` : "--",
    };
  });
};

export const loadAssignmentsReportPlaMetaRow = async (
  tenantName: string,
  route: string
): Promise<PersonalizedLessonAssignmentMetaRow | null> => {
  const { data, error } = await getChoresClient().getAssessments(tenantName);
  if (error) throw error;

  const reports = (data?.reports ?? []).filter(isPersonalizedLessonAssignment);
  if (reports.length === 0) return null;

  const totals = reports.reduce<{ totalLearners: number; completed: number }>(
    (accumulator, report) => {
      accumulator.totalLearners += report.totalLearners || 0;
      accumulator.completed += report.completedCount || 0;
      return accumulator;
    },
    { totalLearners: 0, completed: 0 }
  );

  return {
    id: "personalized-lesson-assignment",
    name: "Personalized Lesson Assignment",
    personalizedLessonAssignment: true,
    personalizedLessonAssignmentPath: route,
    status: "Ongoing",
    progress: { required: totals.totalLearners, requiredPassed: totals.completed },
  };
};
